import React from 'react';
import { View, StyleSheet, FlatList } from 'react-native';
import {
  Text,
  TextInput,
  Card,
  Button,
  IconButton,
  useTheme,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import { ImageUpload } from './ImageUpload';

export function RecipeImages() {
  const theme = useTheme();
  const { watch, setValue, control } = useFormContext();

  const images = watch('images') || [];
  const videoUrl = watch('video_url') || '';

  const addImage = (url: string) => {
    const newImages = [...images, url];
    setValue('images', newImages);
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_: string, i: number) => i !== index);
    setValue('images', newImages);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const newImages = [...images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setValue('images', newImages);
  };

  const renderImageItem = ({
    item,
    index,
  }: {
    item: string;
    index: number;
  }) => (
    <Card style={styles.imageCard}>
      <Card.Cover source={{ uri: item }} style={styles.imagePreview} />
      <Card.Actions style={styles.imageActions}>
        <View style={styles.imagePosition}>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {index === 0 ? 'Main Image' : `Image ${index + 1}`}
          </Text>
        </View>
        <View style={styles.imageControls}>
          {index > 0 && (
            <IconButton
              icon='arrow-left'
              size={16}
              onPress={() => moveImage(index, index - 1)}
            />
          )}
          {index < images.length - 1 && (
            <IconButton
              icon='arrow-right'
              size={16}
              onPress={() => moveImage(index, index + 1)}
            />
          )}
          <IconButton
            icon='delete'
            size={16}
            onPress={() => removeImage(index)}
            iconColor={theme.colors.error}
          />
        </View>
      </Card.Actions>
    </Card>
  );

  return (
    <View style={styles.container}>
      {/* Main Recipe Images */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineSmall' style={styles.sectionTitle}>
            Recipe Images
          </Text>

          {images.length > 0 && (
            <View style={styles.imagesSection}>
              <Text variant='titleMedium' style={styles.subsectionTitle}>
                Current Images ({images.length})
              </Text>
              <FlatList
                data={images}
                keyExtractor={(item, index) => `${item}-${index}`}
                renderItem={renderImageItem}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.imagesList}
              />
            </View>
          )}

          <View style={styles.uploadSection}>
            <Text variant='titleMedium' style={styles.uploadTitle}>
              {images.length === 0
                ? 'Add Main Recipe Image'
                : 'Add Another Image'}
            </Text>
            <ImageUpload onImageUploaded={addImage} onImageRemoved={() => {}} />
            {images.length === 0 && (
              <Text
                variant='bodySmall'
                style={[
                  styles.uploadHint,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                The first image will be used as the main recipe photo
              </Text>
            )}
          </View>
        </Card.Content>
      </Card>

      {/* Video URL */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineSmall' style={styles.sectionTitle}>
            Recipe Video (Optional)
          </Text>

          <Controller
            control={control}
            name='video_url'
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                label='Video URL'
                value={value || ''}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder='https://youtube.com/watch?v=... or https://vimeo.com/...'
                style={styles.input}
              />
            )}
          />

          <Text
            variant='bodySmall'
            style={[styles.videoHint, { color: theme.colors.onSurfaceVariant }]}
          >
            Supports YouTube, Vimeo, and other video platforms
          </Text>

          {videoUrl ? (
            <View style={styles.videoPreview}>
              <Text variant='titleMedium' style={styles.previewTitle}>
                Video Preview
              </Text>
              <Card style={styles.videoCard}>
                <Card.Content style={styles.videoContent}>
                  <Text
                    variant='bodyMedium'
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    Video will be embedded when published
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[styles.videoUrl, { color: theme.colors.primary }]}
                  >
                    {videoUrl}
                  </Text>
                </Card.Content>
              </Card>
            </View>
          ) : (
            <Text
              variant='bodySmall'
              style={[
                styles.videoHint,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              No video added
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Image & Video Tips
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Use high-quality, well-lit photos{'\n'}• Show the final result and
            key steps{'\n'}• The first image becomes your recipe's main photo
            {'\n'}• Videos should be short and focused on technique{'\n'}•
            Consider showing before/after comparisons
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
  },
  card: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 16,
  },
  subsectionTitle: {
    marginBottom: 12,
  },
  imagesSection: {
    marginBottom: 24,
  },
  imagesList: {
    paddingRight: 16,
  },
  imageCard: {
    width: 200,
    marginRight: 12,
  },
  imagePreview: {
    height: 150,
  },
  imageActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  imagePosition: {
    flex: 1,
  },
  imageControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  uploadSection: {
    marginTop: 16,
  },
  uploadTitle: {
    marginBottom: 12,
  },
  uploadHint: {
    marginTop: 8,
    textAlign: 'center',
  },
  input: {
    marginBottom: 8,
  },
  videoHint: {
    marginTop: 4,
    marginBottom: 16,
  },
  videoPreview: {
    marginTop: 16,
  },
  previewTitle: {
    marginBottom: 8,
  },
  videoCard: {
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  videoContent: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  videoUrl: {
    marginTop: 8,
    textAlign: 'center',
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
